/**
 * React Query хук для загрузки уведомлений текущего пользователя
 */

import type { UseQueryResult } from '@tanstack/react-query';
import { useAuthQuery } from '@/hooks';
import { notificationQueryKeys, handleApiError } from '@/lib';
import { supabase } from '@/lib';
import type { Notification } from '../model';
import { mapRowToNotification } from '../lib';

export function useNotificationsQuery(userId?: string): UseQueryResult<Notification[], Error> {
  return useAuthQuery(userId, {
    queryKey: notificationQueryKeys.notifications(userId),
    queryFn: async () => {
      if (!userId) {
        return [];
      }

      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          actor:actor_id (
            id,
            display_name,
            last_name,
            username,
            avatar_url
          ),
          wishlist:wishlist_id (
            id,
            title
          ),
          wish:wish_id (
            id,
            title,
            image_urls
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        // Таблица notifications ещё не создана — возвращаем пустой массив
        if (error.code === 'PGRST205' || error.code === '42P01') {
          return [];
        }
        handleApiError(error, { consolePrefix: 'Ошибка загрузки уведомлений' });
        throw error;
      }

      return (data || []).map(mapRowToNotification);
    },
    // Уведомления - часто меняющиеся данные, staleTime меньше
    staleTime: 1 * 60 * 1000, // 1 минута
    gcTime: 5 * 60 * 1000, // 5 минут
  });
}